import { CustomerItemAPI } from '../../types';

/**
 * Canonicalizes a physical-item identifier for portal UI/state.
 * Trims outer whitespace while preserving internal spaces.
 * Missing or whitespace-only values become an empty string.
 */
export function normalizePhysicalItemIdentifier(
  value: string | null | undefined,
): string {
  return value?.trim() ?? '';
}

/** Quantity alone is not inventory tracking. Historical placeholders have no identity. */
export function hasPhysicalItemTrackingValues(item: {
  lotNumber?: string | null;
  serialNumber?: string | null;
  expirationDate?: string | Date | null;
}): boolean {
  return Boolean(
    normalizePhysicalItemIdentifier(item.lotNumber) ||
      normalizePhysicalItemIdentifier(item.serialNumber) ||
      item.expirationDate,
  );
}

export function getPhysicalItemIdentifierComparisonKey(
  value: string | null | undefined,
): string {
  return normalizePhysicalItemIdentifier(value).toUpperCase();
}

export function physicalItemIdentifiersEqual(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  return (
    getPhysicalItemIdentifierComparisonKey(left) ===
    getPhysicalItemIdentifierComparisonKey(right)
  );
}

export function includesPhysicalItemIdentifier(
  values: readonly string[],
  value: string | null | undefined,
): boolean {
  return values.some((candidate) =>
    physicalItemIdentifiersEqual(candidate, value),
  );
}

export function findPhysicalItemIdentifierKey(
  values: Record<string, unknown>,
  value: string | null | undefined,
): string | undefined {
  return Object.keys(values).find((candidate) =>
    physicalItemIdentifiersEqual(candidate, value),
  );
}

export function getHasInventoryTrackingSetting(
  customerItemSettings: CustomerItemAPI | null,
) {
  return Boolean(
    customerItemSettings?.serialNumberTrackPreference ||
      customerItemSettings?.lotNumberTrackPreference ||
      customerItemSettings?.expirationDateTrackPreference,
  );
}

export function getTotalPrimaries({
  primaryUnitsPerUnitOfMeasure,
  unitOfMeasureQuantity,
}: {
  primaryUnitsPerUnitOfMeasure?: number | null;
  unitOfMeasureQuantity?: number | null;
}): number | null {
  const primaryUnits = Number(primaryUnitsPerUnitOfMeasure) || 0;
  const uomQty = Number(unitOfMeasureQuantity) || 0;

  if (!primaryUnits && !uomQty) return null;
  return primaryUnits * uomQty;
}

/**
 * Converts a quantity entered for a scanned UOM into the primary-unit limit
 * expected by the physical-item tracking forms.
 *
 * A blank quantity means the user scanned a whole moveable unit. In that case
 * the tracking form must use the quantity returned by its inventory endpoint.
 */
export function getPhysicalItemPrimaryQuantityLimit({
  unitOfMeasureQuantity,
  primaryUnitsPerUnitOfMeasure,
}: {
  unitOfMeasureQuantity: number | '' | null | undefined;
  primaryUnitsPerUnitOfMeasure: number | null | undefined;
}): number | undefined {
  if (unitOfMeasureQuantity === '' || unitOfMeasureQuantity == null) {
    return undefined;
  }

  return unitOfMeasureQuantity * (primaryUnitsPerUnitOfMeasure ?? 1);
}
